import { useMemo, useState } from "react";
import type { MediaItem } from "@/mocks/media";
import { buildEpisodes } from "@/hooks/useMediaQuery";
import { useToast } from "@/components/feature/Toast";

export default function EpisodeList({ item }: { item: MediaItem }) {
  const groups = useMemo(() => buildEpisodes(item), [item]);
  const [activeGroup, setActiveGroup] = useState(0);
  const { showToast } = useToast();
  const current = groups[activeGroup] ?? groups[0];
  const isSingle = item.episodes <= 1;

  return (
    <section className="rounded-lg border border-background-200 bg-background-50 p-4 md:p-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="flex items-center gap-2 font-heading text-sm font-bold text-foreground-950 md:text-base">
          <i className="ri-list-ordered flex h-4 w-4 items-center justify-center text-accent-600" />
          {isSingle ? "正片信息" : `剧集目录 · 共 ${item.episodes} 集`}
        </h2>
        <span className="flex items-center gap-1.5 rounded-full bg-secondary-100 px-2.5 py-1 font-label text-[10px] text-secondary-800">
          <i className="ri-information-line flex h-3.5 w-3.5 items-center justify-center" />
          演示目录 · 暂不支持在线播放
        </span>
      </div>

      {!isSingle && groups.length > 1 ? (
        <div className="mt-4 flex flex-wrap gap-1.5">
          {groups.map((group, index) => (
            <button
              key={group.group}
              type="button"
              onClick={() => setActiveGroup(index)}
              className={`cursor-pointer whitespace-nowrap rounded-md px-2.5 py-1 text-[11px] transition-colors ${
                index === activeGroup
                  ? "bg-primary-500 font-medium text-background-50"
                  : "bg-background-100 text-foreground-600 hover:bg-background-200"
              }`}
            >
              {group.group}
            </button>
          ))}
        </div>
      ) : null}

      <div className="mt-4 grid grid-cols-4 gap-2 sm:grid-cols-6 md:grid-cols-8">
        {(current?.items ?? [1]).map((num) => (
          <button
            key={num}
            type="button"
            onClick={() =>
              showToast(isSingle ? "该作品为正片演示条目，暂不支持在线播放" : `第 ${num} 集为目录演示，暂不支持在线播放`)
            }
            className="group flex h-11 cursor-pointer flex-col items-center justify-center rounded-md border border-background-200 bg-background-100/60 transition-colors hover:border-accent-300 hover:bg-accent-50"
          >
            <span className="font-label text-[12px] font-medium text-foreground-800 group-hover:text-accent-700">
              {isSingle ? "正片" : num}
            </span>
            <span className="font-label text-[9px] text-foreground-400 group-hover:text-accent-600">
              {isSingle ? item.duration : "待播"}
            </span>
          </button>
        ))}
      </div>

      <p className="mt-4 flex items-start gap-2 rounded-md bg-background-100/70 px-3 py-2.5 text-[11px] leading-relaxed text-foreground-500">
        <i className="ri-shield-keyhole-line mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center text-foreground-400" />
        本站仅聚合影视资料与索引信息，不提供影片下载与在线播放服务。剧集目录为资料展示用途，点击仅作提示演示。
      </p>
    </section>
  );
}